import { useEffect, useRef, useState } from 'react';

interface IUseImagePreload {
  isLoaded: boolean;
  isError: boolean;
}

export function preloadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = img.onabort = function () {
      reject(src);
    };
    img.src = src;
  });
}

export function useImagePreload(image: string): IUseImagePreload {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const isCancelledRef = useRef<boolean>(false);

  useEffect(() => {
    isCancelledRef.current = false;

    async function effect() {
      if (isCancelledRef.current) {
        return;
      }

      preloadImage(image)
        .then(() => setIsLoaded(true))
        .catch(() => setIsError(true));
    }

    effect();

    return () => {
      isCancelledRef.current = true;
    };
  }, [image]);

  return { isLoaded, isError };
}

export function useImagesPreloader(imageList: string[]) {
  const [isImagesPreloaded, setIsImagesPreloaded] = useState<boolean>(false);
  const isCancelledRef = useRef<boolean>(false);

  useEffect(() => {
    isCancelledRef.current = false;

    async function effect() {
      if (isCancelledRef.current) {
        return;
      }

      const imagesPromiseList: Promise<HTMLImageElement>[] = [];
      for (const i of imageList) {
        imagesPromiseList.push(preloadImage(i));
      }

      try {
        await Promise.allSettled(imagesPromiseList);

        if (isCancelledRef.current) {
          return;
        }

        setIsImagesPreloaded(true);
      } catch (error) {}
    }

    if (imageList.length) {
      effect();
    }

    return () => {
      isCancelledRef.current = true;
    };
  }, [imageList]);

  return { isImagesPreloaded };
}
